import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowSquareOut as ExternalLink, Umbrella, CalendarBlank as CalendarDays, ArrowCounterClockwise as RotateCcw, Lock, PlayCircle, SignOut as LogOut, CaretRight as ChevronRight, CaretDown as ChevronDown, DeviceMobile as Smartphone, ArrowLeft, Compass, Medal as Award, Question as HelpCircle, Gear as SettingsIcon, ClockCounterClockwise as History, Eye, EyeSlash, Wrench, ChatCircle as ChatBubble } from "@phosphor-icons/react";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { FeedbackPulseCard } from "../components/FeedbackPulseCard";
import { RacerBadges } from "../components/RacerBadges";
import { usePrivacy, setPrivacy } from "../lib/privacy";
import { useSheetReconnect } from "../hooks/useSheetReconnect";

const API = process.env.REACT_APP_BACKEND_URL;
const COLOR = {
  bg: "var(--pr-bg)", ink: "var(--pr-ink)", ink2: "var(--pr-ink2)", muted: "var(--pr-muted)",
  line: "var(--pr-line)", card: "var(--pr-card)", gold: "var(--pr-gold)", green: "var(--pr-green)",
};

export default function CaddyMorePage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [view, setView] = useState("loading");
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [expanded, setExpanded] = useState(null); // "goal" | "badges" | null
  const privacyOn = usePrivacy();
  const { needsReconnect } = useSheetReconnect();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/summary`, { credentials: "include" });
        if (res.status === 401 || res.status === 403) { navigate("/app"); return; }
        if (!res.ok) { setView("error"); return; }
        setSummary(await res.json());
        setView("ready");
      } catch {
        setView("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInstalled(window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
    const handler = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

  const handleInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setInstallEvent(null);
  };

  const signOut = async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" }); } catch { /* noop */ }
    navigate("/app");
  };

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }} data-testid="caddy-more-loading">
        <p className="text-sm" style={{ color: COLOR.ink2 }}>One sec…</p>
      </div>
    );
  }

  const isSprint = summary?.entitlements?.mode === "sprint";
  const goalText = summary?.goal_statement
    ? String(summary.goal_statement).replace(/\bin\s+12\s+months?\b/gi, "this year")
    : null;
  const badges = summary?.badges;
  const clearedCount = summary?.streak?.cleared || 0;

  const cls = "flex items-center gap-3 px-4 py-3 active:bg-black/5 transition-colors w-full text-left";
  const sep = (i) => ({ borderTop: i === 0 ? "none" : `1px solid ${COLOR.line}` });
  const rowInner = ({ icon: Icon, label, locked, alert, chevron = "right", isOpen }) => (
    <>
      <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "var(--pr-bg)", opacity: locked ? 0.5 : 1 }}>
        <Icon className="w-4 h-4" style={{ color: COLOR.ink }} />
      </span>
      <span className="flex-1 min-w-0" style={locked ? { opacity: 0.6 } : undefined}>
        <span className="block text-sm font-bold flex items-center gap-1.5" style={{ color: COLOR.ink }}>
          {label}{locked && <Lock className="w-3 h-3" style={{ color: COLOR.muted }} />}
        </span>
      </span>
      {alert && (
        <span data-testid="more-alert-settings" title="Action needed: reconnect Google" className="flex-shrink-0 inline-flex items-center justify-center mr-1"
          style={{ width: 18, height: 18, borderRadius: "50%", background: "#DE3C4B", color: "#fff", fontSize: 12, fontWeight: 800, lineHeight: 1 }}>!</span>
      )}
      {chevron === "down" ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: COLOR.muted }} />
        : <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: COLOR.muted }} />}
    </>
  );
  const NavRow = ({ item, i }) => {
    const { to, href, onClick, testid } = item;
    if (onClick) return <button key={testid} type="button" onClick={onClick} className={cls} style={sep(i)} data-testid={testid}>{rowInner(item)}</button>;
    if (href) return <a key={testid} href={href} target="_blank" rel="noreferrer" className={cls} style={sep(i)} data-testid={testid}>{rowInner(item)}</a>;
    return <Link key={testid} to={to} className={cls} style={sep(i)} data-testid={testid}>{rowInner(item)}</Link>;
  };
  const SectionHead = ({ children, ...p }) => (
    <p className="text-[10px] font-bold tracking-widest uppercase mb-2 mt-5 px-1" style={{ color: COLOR.muted }} {...p}>{children}</p>
  );
  const askCaddy = () => { window.dispatchEvent(new Event("caddy:show-bubble")); navigate("/app"); };

  const relayItems = [
    { to: "/app/history", icon: History, label: "Entry History", testid: "more-history" },
    { to: "/app/monthly", icon: CalendarDays, label: "Monthly Review", locked: isSprint, testid: "more-monthly" },
    { to: "/app/timeoff", icon: Umbrella, label: "Time Off & PTO", testid: "more-timeoff" },
  ];
  const planItems = [
    { onClick: askCaddy, icon: ChatBubble, label: "Ask Caddy", testid: "more-ask-caddy" },
    { to: "/app/resources", icon: Wrench, label: "Library Resources", locked: isSprint, testid: "more-resources" },
    { to: "/app?primer=1", icon: HelpCircle, label: "How the race works", testid: "more-how-it-works" },
    { to: "/app?tour=1", icon: PlayCircle, label: "Replay welcome tour", testid: "more-replay-tour" },
  ];

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }} data-testid="caddy-more-page">
      <div className="max-w-md mx-auto px-5 pt-6 pb-28 lg:pb-10">
        <div className="flex items-center gap-1.5 -ml-2 mb-2">
          <p className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap" style={{ color: COLOR.muted }} data-testid="caddy-more-app-label">
            Payday Relay
          </p>
        </div>
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>More</h1>
          <Link to="/app" className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: COLOR.ink2 }} data-testid="more-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Daily
          </Link>
        </div>

        {/* My Relay */}
        <SectionHead data-testid="more-head-relay">My Relay</SectionHead>
        <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pr-card)", borderColor: COLOR.line }} data-testid="more-section-relay">
          {relayItems.map((item, i) => <NavRow key={item.testid} item={item} i={i} />)}
          {(badges?.length || clearedCount > 0) && (
            <div>
              <button type="button" onClick={() => setExpanded(expanded === "badges" ? null : "badges")} className={cls} style={sep(99)} aria-expanded={expanded === "badges"} data-testid="more-badges">
                {rowInner({ icon: Award, label: "Achievements", chevron: expanded === "badges" ? "down" : "right" })}
              </button>
              {expanded === "badges" && (
                <div className="px-4 py-4" style={{ borderTop: `1px solid ${COLOR.line}`, background: "var(--pr-bg)" }} data-testid="more-badges-body">
                  <RacerBadges badges={badges || []} cleared={clearedCount} />
                </div>
              )}
            </div>
          )}
          {summary?.sheet_url && <NavRow item={{ href: summary.sheet_url, icon: ExternalLink, label: "Open my sheet", testid: "more-open-sheet" }} i={99} />}
          {goalText && (
            <div>
              <button type="button" onClick={() => setExpanded(expanded === "goal" ? null : "goal")} className={cls} style={sep(99)} aria-expanded={expanded === "goal"} data-testid="more-goal">
                {rowInner({ icon: Compass, label: "My goal", chevron: expanded === "goal" ? "down" : "right" })}
              </button>
              {expanded === "goal" && (
                <div className="px-4 py-4" style={{ background: "var(--pr-ink-surface)", color: "#FFFFFF", borderTop: `1px solid ${COLOR.line}` }} data-testid="more-goal-body">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--pr-gold)" }}>My goal</p>
                  <p className="text-xs mt-2 leading-relaxed" style={{ color: "rgba(255,255,255,0.9)" }} data-testid="more-goal-text">{goalText}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Plan & help */}
        <SectionHead data-testid="more-head-plan">Plan &amp; help</SectionHead>
        <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pr-card)", borderColor: COLOR.line }} data-testid="more-section-plan">
          {planItems.map((item, i) => <NavRow key={item.testid} item={item} i={i} />)}
        </div>

        {/* Account */}
        <SectionHead data-testid="more-head-account">Account</SectionHead>
        <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pr-card)", borderColor: COLOR.line }} data-testid="more-section-account">
          <NavRow item={{ to: "/app/settings", icon: SettingsIcon, label: "Settings", testid: "more-settings", alert: needsReconnect }} i={0} />
          {/* Privacy Mode toggle */}
          <div className="flex items-center justify-between gap-3 px-4 py-3" style={{ borderTop: `1px solid ${COLOR.line}` }} data-testid="more-privacy-row">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "var(--pr-bg)" }}>
                {privacyOn ? <EyeSlash className="w-4 h-4" style={{ color: COLOR.ink }} /> : <Eye className="w-4 h-4" style={{ color: COLOR.ink }} />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold" style={{ color: COLOR.ink }}>Privacy Mode</span>
                <span className="block text-[11px] leading-snug" style={{ color: COLOR.ink2 }}>Hide every dollar amount while recording or screen-sharing.</span>
              </span>
            </div>
            <button type="button" role="switch" aria-checked={privacyOn} aria-label="Creator Privacy Mode" onClick={() => setPrivacy(!privacyOn)}
              className="relative w-11 h-6 rounded-full flex-shrink-0 transition-colors" style={{ background: privacyOn ? COLOR.ink : "var(--pr-line)" }} data-testid="more-privacy-toggle">
              <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: privacyOn ? "translateX(20px)" : "translateX(0)" }} />
            </button>
          </div>
          <div className="px-4 py-3 empty:hidden" style={{ borderTop: `1px solid ${COLOR.line}` }} data-testid="more-beta-pulse">
            <FeedbackPulseCard refreshKey={summary?.email || ""} />
          </div>
          <button onClick={signOut} className={cls} style={{ borderTop: `1px solid ${COLOR.line}` }} data-testid="more-signout">
            {rowInner({ icon: LogOut, label: "Sign out" })}
          </button>
        </div>

        {/* Start a Fresh Relay — set apart so it's never a mis-tap */}
        <div className="mt-10 pt-5" style={{ borderTop: `1px dashed ${COLOR.line}` }}>
          <Link
            to={isSprint ? "/start/season-pass" : "/app?fresh=1"}
            className="flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm border"
            style={{ borderColor: "#DE3C4B", color: "#DE3C4B", background: "var(--pr-card)" }}
            data-testid="more-fresh-start"
          >
            <RotateCcw className="w-4 h-4" /> Start a Fresh Relay{isSprint && <Lock className="w-3 h-3" />}
          </Link>
          <p className="text-[11px] text-center leading-snug mt-2 px-4" style={{ color: COLOR.muted }} data-testid="more-fresh-start-note">
            Resets your race to week 1. Your targets and weekly progress start over; you'll be asked to confirm first, and your Google Sheet history stays untouched.
          </p>
        </div>

        <p className="text-[11px] text-center leading-relaxed" style={{ color: COLOR.muted }}>
          Payday Relay is an educational tool. Not financial, tax, or investment advice.{" "}
          <Link to="/terms" className="underline">Terms</Link>
        </p>
      </div>
      <CaddyBottomNav active="more" sprint={isSprint} />
    </div>
  );
}
